import { useEffect, useState } from 'react';
import { BookOpen, Clock, ArrowLeft, Users, Calendar as CalendarDateIcon, GraduationCap, Search, Plus, MoreVertical, MapPin, ChevronRight, Zap, AlertCircle, TrendingUp, Target, Award, CheckCircle, ArrowRight, AlertTriangle, FastForward, Pause, Download, BarChart3, Lightbulb, Settings, Shield, ClipboardList, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTeacher } from '@/context/TeacherContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn, getButtonColor } from '@/lib/utils';
import { trackEvent, attachUserProps } from '@/analytics/analytics';
import { FormModal } from '@/components/ui/form-modal';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Progress } from '@/components/ui/progress';
import { CompletePagination } from '@/components/ui/complete-pagination';
import { Textarea } from '@/components/ui/textarea';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';

// Helper function to extract grade and division from class name
const getGradeAndDivision = (className) => {
  if (!className) return '';
  // Extract "Grade XA" or "Grade XB" pattern from strings like "Physics - Grade 8A"
  const match = className.match(/Grade\s+(\d+[A-Z])/i);
  if (match) {
    return `Grade ${match[1]}`;
  }
  // Fallback: if no "Grade" prefix, try to extract just the grade and division
  const fallbackMatch = className.match(/(\d+[A-Z])/);
  if (fallbackMatch) {
    return `Grade ${fallbackMatch[1]}`;
  }
  return className; // Return original if no pattern matches
};

export default function TeacherMyClasses() {
  const navigate = useNavigate();
  const { currentClass, teacherTheme } = useTeacher();
  const { toast } = useToast();
  const isDark = teacherTheme === 'dark';
  const [searchStudent, setSearchStudent] = useState('');
  const [performanceFilter, setPerformanceFilter] = useState('all'); // 'all', 'good', 'needs-help'
  const [students, setStudents] = useState([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);

  // Add Class Modal State
  const [isAddClassModalOpen, setIsAddClassModalOpen] = useState(false);
  const [isAddingClass, setIsAddingClass] = useState(false);
  const [newClassData, setNewClassData] = useState({
    name: '',
    subject: '',
    room: '',
    schedule: ''
  });

  // Tasks functionality state
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [newTaskData, setNewTaskData] = useState({
    title: '',
    description: '',
      subject: 'Physics',
    chapter: '',
    dueDate: '',
    assignedTo: 'Class 10A'
  });

  // Static demo tasks data
  const tasks = [
    {
      id: 1,
      title: "Solar System Research Project",
      description: "Students need to research and create a presentation about the solar system, including all planets and their characteristics.",
      subject: "Physics",
      chapter: "Solar System",
      dueDate: "2024-01-15",
      assignedTo: "Class 10A",
      status: "active",
      submissions: 12,
      totalStudents: 25
    },
    {
      id: 2,
      title: "Physics Lab Report",
      description: "Complete the lab report for the gravity experiment conducted last week.",
      subject: "Physics",
      chapter: "Gravitation",
      dueDate: "2024-01-20",
      assignedTo: "Class 10B",
      status: "active",
      submissions: 8,
      totalStudents: 20
    },
    {
      id: 3,
      title: "Math Problem Set",
      description: "Solve problems 1-20 from Chapter 5: Quadratic Equations",
      subject: "Mathematics",
      chapter: "Quadratic Equations",
      dueDate: "2024-01-12",
      assignedTo: "Class 9A",
      status: "completed",
      submissions: 18,
      totalStudents: 18
    }
  ];

  const handleAddClass = async () => {
    setIsAddingClass(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsAddingClass(false);
    setIsAddClassModalOpen(false);
    setNewClassData({ name: '', subject: '', room: '', schedule: '' });
    try { trackEvent('class_created', attachUserProps({ page: 'teacher_my_classes' })); } catch (e) {}
  };

  const handleCreateTask = async () => {
    try {
      setIsCreatingTask(true);
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast({
        title: "Success!",
        description: "Task created successfully.",
      });
      setIsCreateTaskModalOpen(false);
      setNewTaskData({
        title: '',
        description: '',
        subject: 'Physics',
        chapter: '',
        dueDate: '',
        assignedTo: 'Class 10A'
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create task. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingTask(false);
      try { trackEvent('task_created', attachUserProps({ page: 'teacher_my_classes' })); } catch (e) {}
    }
  };

  const handleDeleteClick = (task) => {
    setTaskToDelete(task);
    setDeleteConfirmOpen(true);
    try { trackEvent('task_delete_initiated', attachUserProps({ task_id: task.id, page: 'teacher_my_classes' })); } catch (e) {}
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast({
        title: "Deleted!",
        description: "Task deleted successfully.",
      });
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
      try { trackEvent('task_deleted', attachUserProps({ page: 'teacher_my_classes' })); } catch (e) {}
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete task. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    return isDark ? 'bg-[hsl(var(--gold-primary))] text-white' : 'bg-[#2B2B2B] text-white';
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'completed': return 'Completed';
      case 'overdue': return 'Overdue';
      default: return 'Unknown';
    }
  };

  const totalPages = Math.ceil(tasks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTasks = tasks.slice(startIndex, endIndex);

  const renderTasksTab = () => {
    const totalPages = Math.ceil(tasks.length / itemsPerPage);
    return (
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Task Management</h2>
            <p className={cn("mt-1 text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Assign and manage tasks for your students</p>
          </div>
          <Button
            onClick={() => { setIsCreateTaskModalOpen(true); try { trackEvent('open_create_task_modal', attachUserProps({ page: 'teacher_my_classes' })); } catch (e) {} }}
            className={cn("h-10 font-medium rounded-lg shadow-md", getButtonColor(isDark))}
          >
            <Plus className="w-5 h-5 mr-2" />
            <span className="font-medium">Create Task</span>
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className={cn("border shadow-sm", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#D4C4A8] to-[#CDB89A] border-[#C9B08A]")}>
            <CardContent className="p-6 flex items-center">
              <div className={cn("p-3 rounded-xl border", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))]" : "bg-[#D4C4A8] border-[#C9B896]")}>
                <ClipboardList className={cn("w-6 h-6", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")} />
              </div>
              <div className="ml-4">
                <p className={cn("text-xs font-medium uppercase tracking-wide", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#3D3D3D]")}>Total Tasks</p>
                <p className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{tasks.length}</p>
              </div>
            </CardContent>
          </Card>

          <Card className={cn("border shadow-sm", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#D4C4A8] to-[#CDB89A] border-[#C9B08A]")}>
            <CardContent className="p-6 flex items-center">
              <div className={cn("p-3 rounded-xl border", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))]" : "bg-[#D4C4A8] border-[#C9B896]")}>
                <CalendarIcon className={cn("w-6 h-6", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")} />
              </div>
              <div className="ml-4">
                <p className={cn("text-xs font-medium uppercase tracking-wide", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#3D3D3D]")}>Active Tasks</p>
                <p className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{tasks.filter(t => t.status === 'active').length}</p>
              </div>
            </CardContent>
          </Card>

          <Card className={cn("border shadow-sm", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#D4C4A8] to-[#CDB89A] border-[#C9B08A]")}>
            <CardContent className="p-6 flex items-center">
              <div className={cn("p-3 rounded-xl border", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))]" : "bg-[#D4C4A8] border-[#C9B896]")}>
                <Users className={cn("w-6 h-6", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")} />
              </div>
              <div className="ml-4">
                <p className={cn("text-xs font-medium uppercase tracking-wide", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#3D3D3D]")}>Total Students</p>
                <p className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>63</p>
              </div>
            </CardContent>
          </Card>

          <Card className={cn("border shadow-sm", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#D4C4A8] to-[#CDB89A] border-[#C9B08A]")}>
            <CardContent className="p-6 flex items-center">
              <div className={cn("p-3 rounded-xl border", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))]" : "bg-[#D4C4A8] border-[#C9B896]")}>
                <Clock className={cn("w-6 h-6", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")} />
              </div>
              <div className="ml-4">
                <p className={cn("text-xs font-medium uppercase tracking-wide", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#3D3D3D]")}>Pending Reviews</p>
                <p className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>20</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tasks List */}
        <Card className={cn("border shadow-sm", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#F5F1EA] to-[#EDE3D2] border border-[#E0D4BF]")}>
          <CardHeader className={cn("border-b py-4", isDark ? "border-[hsl(var(--card-border))] bg-[hsl(var(--card))]/50" : "border-[#E0D4BF] bg-[#F5F1EA]/50")}>
            <CardTitle className={cn("text-lg font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>All Tasks</CardTitle>
            <p className={cn("text-sm mt-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Manage and track student assignments</p>
          </CardHeader>

          <div className={cn("divide-y", isDark ? "divide-[hsl(var(--card-border))]" : "divide-[#E0D4BF]")}>
            {paginatedTasks.length === 0 ? (
              <EmptyState
                title="No tasks yet"
                description="Create your first task to assign to students"
                actionLabel="Create Task"
                onAction={() => setIsCreateTaskModalOpen(true)}
              />
            ) : (
              paginatedTasks.map((task) => (
                <div key={task.id} className={cn("p-6 transition-colors", isDark ? "hover:bg-[hsl(var(--card-border))]/30" : "hover:bg-[#E0D4BF]/30")}>
                  <div className="flex flex-col lg:flex-row items-start justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className={cn("text-lg font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{task.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${getStatusColor(task.status)}`}>
                          {getStatusText(task.status)}
                        </span>
                      </div>

                      <p className={cn("mb-4 text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>{task.description}</p>

                      <div className={cn("flex flex-wrap items-center gap-6 text-xs", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#8B7B6B]")}>
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-4 h-4" />
                          <span>{task.subject} - {task.chapter}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>{task.assignedTo}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CalendarDateIcon className="w-4 h-4" />
                          <span>Due: {task.dueDate}</span>
                        </div>
                      </div>
                    </div>

                    <div className="w-full lg:w-64 space-y-3">
                      <div className={cn("rounded-lg p-4 border", isDark ? "bg-[hsl(var(--card-border))]/20 border-[hsl(var(--card-border))]" : "bg-white/50 border-[#E0D4BF]")}>
                        <div className="flex justify-between items-center mb-2">
                          <p className={cn("text-xs font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Submissions</p>
                          <p className={cn("text-sm font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{task.submissions}/{task.totalStudents}</p>
                        </div>
                        <Progress value={(task.submissions / task.totalStudents) * 100} className="h-2" />
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteClick(task)}
                        className={cn(
                          "w-full border-red-500/20 text-red-500 hover:bg-red-500/10 hover:text-red-600 hover:border-red-500/30",
                          isDark ? "hover:bg-red-500/20" : ""
                        )}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Task
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Pagination */}
        {tasks.length > itemsPerPage && (
          <CompletePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            totalItems={tasks.length}
            showItemsPerPage={true}
          />
        )}
      </div>
    );
  };

  const token = getAuthToken('teacher');

  useEffect(() => {
    const loadStudents = async () => {
      if (!token) return;
      setIsLoadingStudents(true);
      try {
        const params = new URLSearchParams();
        if (currentClass?.id) params.append('class_id', currentClass.id);
        if (performanceFilter === 'good') params.append('filter', 'on_track');
        else if (performanceFilter === 'needs-help') params.append('filter', 'needs_help');
        const queryString = params.toString() ? `?${params.toString()}` : '';
        const res = await axios.get(apiUrl(`v1/teachers/students${queryString}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const apiStudents = res?.data?.students || [];
        setStudents(apiStudents.map((s) => ({
          id: s.id,
          name: s.name,
          class: s.class,
          avgScore: s.avg_score || 0,
          performanceTrend: s.performance_trend || 'stable',
          lastAssessment: s.last_assessment,
        })));
      } catch (e) {
        console.error('Failed to load students', e);
        setStudents([]);
      } finally {
        setIsLoadingStudents(false);
      }
    };
    loadStudents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClass?.id, performanceFilter]);

  const filteredStudents = students.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchStudent.toLowerCase());
    return matchesSearch;
  });

  const [expandedStudentId, setExpandedStudentId] = useState(null);

  const getColorClasses = (color) => {
    return isDark ? 'bg-[hsl(var(--gold-primary))] text-white' : 'bg-[#2B2B2B] text-white';
  };




  const renderComingSoonTab = (tabName) => (
    <div className="text-center py-12">
      <Zap className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
      <h3 className="text-lg font-semibold text-foreground mb-2">{tabName} Coming Soon</h3>
      <p className="text-muted-foreground">This feature will be available in the next update</p>
    </div>
  );

  return (
    <>
      <div className={cn("flex flex-col h-full p-8 overflow-y-auto", isDark ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]")}>
      <div className="max-w-7xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/teacher/dashboard')}
              className={cn("rounded-xl", isDark ? "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/60" : "text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EBE3]/60")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={cn("text-4xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Student Insights</h1>
              <p className={cn("mt-1 text-base font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Analyze and manage your students by class</p>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 w-full">
          <button
            onClick={() => {}}
            className={cn(
              "flex-1 px-4 py-3 rounded-lg font-medium text-sm transition-all h-10 shadow-sm",
              getButtonColor(isDark)
            )}
          >
            <Users className="w-4 h-4 mr-2 inline" />
            Student Analysis & Performance
          </button>
        </div>

        <div className="space-y-8">
              {/* Student Analysis Section */}
              <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#F5F1EA] to-[#EDE3D2] border-[#E0D4BF]")}>
                <CardHeader className={cn("py-5 border-b rounded-t-2xl", isDark ? "bg-[hsl(var(--card-border))]/10 border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}>
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div>
                      <CardTitle className={cn("text-xl font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Student Analysis</CardTitle>
                      {currentClass && <p className={cn("text-sm mt-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Class: {currentClass.fullName || currentClass.name}</p>}
                    </div>
                    <div className="flex space-x-3 w-full sm:w-auto">
                      <div className="relative flex-1 sm:flex-none">
                        <Search className={cn("w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
            <Input
                          type="text"
                          placeholder="Search students..."
                          value={searchStudent}
                          onChange={(e) => setSearchStudent(e.target.value)}
                          className={cn("pl-10 w-full sm:w-64 text-sm", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}
            />
          </div>
                    </div>
        </div>
                </CardHeader>
                <CardContent className="p-6">
                {/* Performance Filter Buttons */}
                <div className="flex flex-wrap gap-2 mb-6">
                  <button
                    onClick={() => { setPerformanceFilter('all'); try { trackEvent('performance_filter_change', attachUserProps({ filter: 'all', page: 'teacher_my_classes' })); } catch (e) {} }}
                    className={cn("px-4 py-2 rounded-lg text-sm font-medium transition-all", performanceFilter === 'all' ? getButtonColor(isDark) : isDark ? "bg-[hsl(var(--card-border))]/50 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--card-border))]" : "bg-[#F0EBE3] text-[#6B6B6B] hover:bg-[#E0D4BF]")}
                  >
                    All Students
                  </button>
                  <button
                    onClick={() => { setPerformanceFilter('good'); try { trackEvent('performance_filter_change', attachUserProps({ filter: 'good', page: 'teacher_my_classes' })); } catch (e) {} }}
                    className={cn("px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2", performanceFilter === 'good' ? getButtonColor(isDark) : isDark ? "bg-[hsl(var(--card-border))]/50 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--card-border))]" : "bg-[#F0EBE3] text-[#6B6B6B] hover:bg-[#E0D4BF]")}
                  >
                    <span>⭐</span> Good
                  </button>
                  <button
                    onClick={() => { setPerformanceFilter('needs-help'); try { trackEvent('performance_filter_change', attachUserProps({ filter: 'needs-help', page: 'teacher_my_classes' })); } catch (e) {} }}
                    className={cn("px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2", performanceFilter === 'needs-help' ? getButtonColor(isDark) : isDark ? "bg-[hsl(var(--card-border))]/50 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--card-border))]" : "bg-[#F0EBE3] text-[#6B6B6B] hover:bg-[#E0D4BF]")}
                  >
                    <span>⚠️</span> Needs Help
                  </button>
                </div>
        {filteredStudents.length === 0 ? (
          <div className={cn("text-center py-12", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
            <p className="text-lg font-medium">No students found</p>
            <p className="text-sm">{currentClass ? `No students in ${currentClass.fullName || currentClass.name}` : 'Try searching for a different name'}</p>
        </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredStudents.map((student) => (
                      <div key={student.id} className={cn("border rounded-xl p-5 shadow-sm transition-all", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-white to-[#F9F5F0] border-[#E0D4BF]")}>
                        {/* Header with name and more icon */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className={cn("w-12 h-12 rounded-full flex items-center justify-center border-2 font-bold text-xs flex-shrink-0", isDark ? "bg-gradient-to-br from-[hsl(var(--gold-primary))] to-[hsl(var(--gold-medium))] border-[hsl(var(--gold-primary))] text-white" : "bg-gradient-to-br from-[#C9A961] to-[#B8985C] border-[#C9A961] text-white")}>{student.name.split(' ').map(n => n[0]).join('')}</div>
                            <div className="flex-1">
                              <h4 className={cn("font-bold text-base leading-tight", isDark ? "text-foreground" : "text-[#2B2B2B]")}>{student.name}</h4>
                              <p className={cn("text-sm font-medium mt-0.5", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{getGradeAndDivision(student.class)}</p>
                            </div>
                </div>
                          <button onClick={() => setExpandedStudentId(expandedStudentId === student.id ? null : student.id)} className={cn("p-2 rounded-lg transition-all", isDark ? "hover:bg-[hsl(var(--card-border))]/50 text-[hsl(var(--muted-foreground))]" : "hover:bg-[#E0D4BF]/60 text-[#6B6B6B]")}>
                            <MoreVertical className="w-5 h-5" />
                          </button>
                </div>

                        {/* Main details */}
                        <div className={cn("flex items-end justify-between py-3 border-t border-b", isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}>
                <div>
                            <p className={cn("text-sm font-bold mb-1", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Performance</p>
                            <p className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")}>{student.avgScore}%</p>
                  </div>
                          <div className={cn("px-3 py-1 rounded text-xs font-semibold", isDark ? "bg-[hsl(var(--gold-primary))] text-white" : "bg-[#2B2B2B] text-white")}>
                            {student.avgScore >= 80 ? '⭐ Good' : '⚠️ Needs Help'}
                  </div>
                </div>

                        {/* Expanded details */}
                        {expandedStudentId === student.id && (
                          <div className="mt-4 pt-4 space-y-3 border-t" style={{borderColor: isDark ? 'hsl(var(--card-border))' : '#E0D4BF'}}>
                            <div className="flex justify-between items-center text-sm">
                              <span className={isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]"}>Last Assessment:</span>
                              <span className={cn("font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                                {student.lastAssessment 
                                  ? new Date(student.lastAssessment).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })
                                  : 'No assessment'}
                              </span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                              <span className={isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]"}>Trend:</span>
                              <span className={cn("font-medium", student.performanceTrend === 'up' ? 'text-green-500' : student.performanceTrend === 'down' ? 'text-red-500' : 'text-yellow-500')}>
                                {student.performanceTrend === 'up' ? '📈 Improving' : student.performanceTrend === 'down' ? '📉 Declining' : '➡️ Stable'}
                              </span>
                            </div>
                            <button onClick={() => { try { trackEvent('view_student_profile', attachUserProps({ student_id: student.id, page: 'teacher_my_classes' })); } catch (e) {} ; navigate(`/teacher/student/${student.id}`); } } className={cn("w-full px-3 py-2 rounded-lg mt-3 text-sm font-medium transition-colors", isDark ? "bg-[hsl(var(--gold-primary))] hover:bg-[hsl(var(--gold-medium))] text-white" : "bg-[#2B2B2B] hover:bg-[#1A1A1A] text-white")}> 
                              View Full Profile
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
        )}
        
              </CardContent>
            </Card>

                  </div>
                  </div>
                </div>

      {/* Add New Class Modal */}
      <FormModal
          isOpen={isAddClassModalOpen}
          onClose={() => setIsAddClassModalOpen(false)}
          onSave={handleAddClass}
          title="Add New Class"
          isLoading={isAddingClass}
          saveLabel="Create Class"
        >
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="className">Class Name</Label>
              <Input
                id="className"
                value={newClassData.name}
                onChange={(e) => setNewClassData({ ...newClassData, name: e.target.value })}
                placeholder="e.g. Grade 10A"
                className="mt-1.5"
              />
                  </div>
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={newClassData.subject}
                onChange={(e) => setNewClassData({ ...newClassData, subject: e.target.value })}
                placeholder="e.g. Physics"
                className="mt-1.5"
                    />
                  </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="room">Room</Label>
                <Input
                  id="room"
                  value={newClassData.room}
                  onChange={(e) => setNewClassData({ ...newClassData, room: e.target.value })}
                  placeholder="e.g. Room 301"
                  className="mt-1.5"
                />
                </div>
              <div>
                <Label htmlFor="schedule">Schedule</Label>
                <Input
                  id="schedule"
                  value={newClassData.schedule}
                  onChange={(e) => setNewClassData({ ...newClassData, schedule: e.target.value })}
                  placeholder="e.g. Mon, Wed 9:00 AM"
                  className="mt-1.5"
                />
        </div>
      </div>
    </div>
        </FormModal>
    </>
  );
}

